import type { ReactNode } from 'react'
import type { TelescopeNight } from '@/lib/server/telescope'

import { useState } from 'react'
import { ChevronDown, Cloud, CloudMoon, Moon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonClass } from '@/lib/ui'
import { formatKey, parseDateKey } from '@/lib/time'
import { formatPrice } from '@/constants/tickets'

export type NightForecast = {
    available: boolean
    cloud: number | null
    seeing: number | null
    summary: string
}

export const ForecastBadge = ({ forecast, className }: { forecast?: NightForecast, className?: string }): ReactNode => {
    if (!forecast || !forecast.available || forecast.cloud === null) {
        return (
            <span className={cn('label-mono inline-flex items-center gap-2 text-star-3', className)}>
                <Moon aria-hidden className='size-3.5' strokeWidth={1.25} />
                {forecast?.summary ?? 'Forecast opens nine days ahead'}
            </span>
        )
    }

    const Icon = forecast.cloud < 35 ? Moon : forecast.cloud < 70 ? CloudMoon : Cloud

    return (
        <span className={cn('label-mono inline-flex items-center gap-2', forecast.cloud < 35 ? 'text-brass' : 'text-star-2', className)}>
            <Icon aria-hidden className='size-3.5' strokeWidth={1.25} />
            {`${forecast.summary} · ${forecast.cloud}% cloud${forecast.seeing ? ` · seeing ${forecast.seeing}/5` : ''}`}
        </span>
    )
}

interface NightListProps {
    nights: TelescopeNight[]
    forecasts: Record<string, NightForecast>
    onBook: (night: TelescopeNight) => void
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Upcoming observing nights; each one opens to show its targets, instruments and what to bring. */
export const NightList = ({ nights, forecasts, onBook }: NightListProps): ReactNode => {
    const [open, setOpen] = useState<string | null>(nights[0]?.id ?? null)

    if (nights.length === 0) {
        return (
            <p className='border-t border-hair pt-8 text-star-2'>
                New telescope nights are being planned. Join the Clear skies list below and you will hear first.
            </p>
        )
    }

    return (
        <ul className='border-t border-hair'>
            {nights.map(night => {
                const expanded = open === night.id
                const { day, month } = parseDateKey(night.nightDate)
                const full = night.remaining === 0
                const panelId = `night-${night.id}`

                return (
                    <li key={night.id} className='border-b border-hair'>
                        <button
                            type='button'
                            aria-expanded={expanded}
                            aria-controls={panelId}
                            onClick={() => setOpen(expanded ? null : night.id)}
                            className='group grid w-full grid-cols-[4.5rem_1fr_auto] items-center gap-4 py-6 text-left md:grid-cols-[6rem_1fr_16rem_8rem_auto] md:gap-8'
                        >
                            <span className='flex flex-col'>
                                <span className='font-heading text-5xl font-light leading-none text-star'>
                                    {day}
                                </span>
                                <span className='label-mono mt-1 text-star-3'>
                                    {`${MONTHS[month - 1]} · ${formatKey(night.nightDate, 'short').split(' ')[0]}`}
                                </span>
                            </span>
                            <span>
                                <span className='label-mono block text-brass'>
                                    {night.theme}
                                </span>
                                <span className='mt-1 block font-heading text-2xl text-star md:text-3xl'>
                                    {night.title}
                                </span>
                                <span className='data-mono mt-1 block text-star-3'>
                                    {`${night.startsLocal} to ${night.endsLocal} · ${night.astronomer}`}
                                </span>
                                <ForecastBadge forecast={forecasts[night.id]} className='mt-3 flex md:hidden' />
                                <span className={cn('label-mono mt-2 block md:hidden', full ? 'text-star-3' : night.remaining <= 4 ? 'text-brass' : 'text-star-2')}>
                                    {full ? 'Full' : `${night.remaining} of ${night.capacity} left`}
                                </span>
                            </span>
                            <ForecastBadge forecast={forecasts[night.id]} className='hidden md:flex' />
                            <span className={cn('label-mono hidden md:block', full ? 'text-star-3' : night.remaining <= 4 ? 'text-brass' : 'text-star-2')}>
                                {full ? 'Full' : `${night.remaining} of ${night.capacity} left`}
                            </span>
                            <ChevronDown aria-hidden className={cn('size-5 text-star-3 transition-transform duration-500', expanded && 'rotate-180 text-brass')} strokeWidth={1.25} />
                        </button>

                        <div
                            id={panelId}
                            inert={!expanded}
                            className={cn('grid transition-[grid-template-rows] duration-700 ease-[cubic-bezier(0.22,0.61,0.36,1)]', expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}
                        >
                            <div className='overflow-hidden'>
                                <div className='pb-10 md:pl-[7rem]'>
                                    <p className='max-w-2xl text-star-2'>
                                        {night.summary}
                                    </p>
                                    <div className='mt-8 grid gap-8 md:grid-cols-3'>
                                        {[
                                            { title: 'What we will look at', items: night.targets },
                                            { title: 'Instruments', items: night.instruments },
                                            { title: 'What to bring', items: night.bring }
                                        ].map(column => (
                                            <div key={column.title}>
                                                <p className='label-mono text-star-3'>
                                                    {column.title}
                                                </p>
                                                <ul className='mt-3 space-y-2'>
                                                    {column.items.map(item => (
                                                        <li key={item} className='flex gap-3 text-[0.95rem] text-star'>
                                                            <span aria-hidden className='mt-[0.6rem] h-px w-3 shrink-0 bg-brass' />
                                                            {item}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                    <div className='mt-10 flex flex-wrap items-center gap-6'>
                                        <button type='button' onClick={() => onBook(night)} disabled={full} className={buttonClass('primary')}>
                                            {full ? 'This night is full' : 'Reserve places'}
                                        </button>
                                        <p className='data-mono text-star-2'>
                                            {`${formatPrice(night.pricePence)} per person · members ${formatPrice(night.memberPricePence)}`}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </li>
                )
            })}
        </ul>
    )
}
